import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../lib/auth'
import { BUILD_ID } from '../lib/stale-chunk'
import { surface } from '../surface'

// Auth-aware nav entry: "Dashboard" once signed in, else "Sign in". Uses the default auth context
// (session: null) when rendered without an AuthProvider — e.g. as a federated remote in the host —
// so it degrades to a "Sign in" link and never throws.
function AuthLink() {
  const { session } = useAuth()
  return (
    <Link to={session ? '/dashboard' : '/login'} className="hover:text-ink">
      {session ? 'Dashboard' : 'Sign in'}
    </Link>
  )
}

// The mark: the aircargo "sky-rim" simultaneous disc (navo24 brand book §01/§06), shared across both
// air surfaces so they read as siblings of the navo24 family. Rings outer→inner match the codified
// aircargo sky-rim key (#2b1d7a #6a4bd6 #4a86ff #e2231a #ff7a1f #ffc42e) + white core.
function Brand() {
  return (
    <a
      href="/"
      className="flex items-center gap-2.5"
      aria-label={`${surface.wordA}${surface.wordB}MCP home`}
    >
      <svg
        width="26"
        height="26"
        viewBox="0 0 100 100"
        aria-hidden="true"
        className="block shrink-0"
      >
        <circle cx="50" cy="50" r="48" fill="#2b1d7a" />
        <circle cx="50" cy="50" r="40" fill="#6a4bd6" />
        <circle cx="50" cy="50" r="32" fill="#4a86ff" />
        <circle cx="50" cy="50" r="24" fill="#e2231a" />
        <circle cx="50" cy="50" r="16" fill="#ff7a1f" />
        <circle cx="50" cy="50" r="8" fill="#ffc42e" />
        <circle cx="50" cy="50" r="3" fill="#fff" />
      </svg>
      <span
        className="text-[17px] font-semibold tracking-tight text-ink"
        style={{ fontFamily: "'Ranade', Georgia, serif" }}
      >
        {surface.wordA}
        <span className="text-signal">{surface.wordB}</span>
      </span>
    </a>
  )
}

export function Layout({ children, onDark }: { children: ReactNode; onDark?: boolean }) {
  const family = [
    { label: 'Ocean rates', href: 'https://freightratesmcp.com' },
    { label: 'Ocean tracking', href: 'https://trackingmcp.com' },
    { label: 'Schedules', href: 'https://schedulesmcp.com' },
  ]
  return (
    <div className="flex min-h-screen flex-col bg-void font-sans text-ink">
      <header
        className={`glass sticky top-0 z-40 border-b transition-colors duration-300 ${
          onDark ? 'tone-dark-vars border-transparent' : 'border-border'
        }`}
      >
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5">
          <Brand />
          <nav className="flex items-center gap-5 text-[13.5px] text-muted">
            <a href={surface.sibling.href} className="hidden hover:text-ink sm:inline">
              {surface.sibling.label}
            </a>
            <a href="https://freightratesmcp.com" className="hidden hover:text-ink md:inline">
              Ocean rates
            </a>
            <a
              href="https://navo24.com/developers/reference/aircargo"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-ink"
            >
              Developers
            </a>
            <Link to="/contact" className="hover:text-ink">
              Contact sales
            </Link>
            <AuthLink />
            <span className="mono hidden rounded-full border border-border px-2.5 py-1 text-[11px] uppercase tracking-wider text-faint lg:inline">
              {surface.domain}
            </span>
          </nav>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="mt-24 border-t border-border">
        <div className="mx-auto grid max-w-6xl gap-8 px-5 py-12 text-sm text-muted md:grid-cols-4">
          <div>
            <div className="text-[15px] font-bold text-ink">
              {surface.wordA}
              <span className="text-signal">{surface.wordB}</span>MCP
            </div>
            <p className="mt-2.5 leading-relaxed">
              {surface.key === 'aircargo'
                ? 'Live air-waybill track & trace across the world’s cargo airlines.'
                : 'Air-freight rate intelligence: chargeable weight, weight bands, all-in per-kg.'}
            </p>
          </div>
          <div>
            <div className="mb-2 font-medium text-ink">Air cargo</div>
            <ul className="space-y-1.5">
              <li>
                <a href="https://aircargomcp.com" className="hover:text-ink">
                  AWB tracking
                </a>
              </li>
              <li>
                <a href="https://airratesmcp.com" className="hover:text-ink">
                  Air rate quotes
                </a>
              </li>
              <li>
                <Link to="/contact" className="hover:text-ink">
                  Contact sales for a quote
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <div className="mb-2 font-medium text-ink">navo24 family</div>
            <ul className="space-y-1.5">
              {family.map((f) => (
                <li key={f.href}>
                  <a href={f.href} className="hover:text-ink">
                    {f.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="mb-2 font-medium text-ink">Prototype</div>
            <p className="leading-relaxed">
              Sample data. AWB <span className="mono text-ink">203-12345675</span> and lane{' '}
              <span className="mono text-ink">HKG→FRA</span> return live results from the dev API.
            </p>
          </div>
        </div>
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-x-5 gap-y-3 border-t border-border px-5 py-6 text-xs text-faint">
          <nav className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <a href="/privacy" className="hover:text-ink">
              Privacy
            </a>
            <a href="/tos" className="hover:text-ink">
              Terms
            </a>
            <a href="https://navo24.com/about" className="hover:text-ink">
              About
            </a>
            <a href="https://navo24.com" className="hover:text-ink">
              navo24 ↗
            </a>
          </nav>
          <span className="mono">
            a Navo24 product · build <span className="text-faint">{BUILD_ID}</span>
          </span>
        </div>
      </footer>
    </div>
  )
}
